/**
 * Image Cropping Utilities
 * Handles image cropping, validation, and conversion for company branding
 */

// Area interface from react-easy-crop
export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Creates a cropped image from the source image
 * Output is EXACTLY 220x90px regardless of input size
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Set canvas to EXACTLY 220x90px
  const targetWidth = 220;
  const targetHeight = 90;
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  // Draw the cropped portion and scale to exact dimensions
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    targetWidth,
    targetHeight
  );

  // Convert canvas to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(blob);
    }, 'image/png');
  });
}

/**
 * Validate image dimensions to prevent pixelation
 * Minimum dimensions should be at least 220x90px
 */
export function validateImageDimensions(
  width: number,
  height: number
): { valid: boolean; message?: string } {
  const minWidth = 220;
  const minHeight = 90;

  if (width < minWidth || height < minHeight) {
    return {
      valid: false,
      message: `Image dimensions too small. Minimum: ${minWidth}x${minHeight}px. Current: ${width}x${height}px. Upload a larger image to prevent pixelation.`
    };
  }

  return { valid: true };
}

/**
 * Convert blob to base64 string for preview display
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert blob to base64'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Read file as data URL
 */
export function readFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Create an image element from source URL
 * Helper function for canvas operations
 */
function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}

/**
 * Validate file type and size
 */
export function validateFile(file: File): { valid: boolean; message?: string } {
  const maxSize = 5 * 1024 * 1024; // 5MB
  const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];

  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      message: 'Invalid file type. Please upload PNG, JPG, or SVG.'
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      message: `File too large. Maximum size: 5MB. Current: ${(file.size / 1024 / 1024).toFixed(2)}MB.`
    };
  }

  return { valid: true };
}

/**
 * Get image dimensions from file
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}
